// bridge.ts — the web → BC Keyboard handoff.
//
// The BC Keyboard is a native iOS extension living in its own sandbox. It can
// only read the App Group container. The container app (this Capacitor shell)
// mirrors these Capacitor Preferences into that App Group on every launch /
// didBecomeActive (see ios/App/App/BCKeyboardBridge.swift). So from the web all
// we do is write four Preferences keys; the native bridge carries them across.
//
// Contract (must match BCKeyboardBridge.swift):
//   bcKeyboardApiKey   → a bc_live_* key (this account; Lite-and-above enforced server-side)
//   bcKeyboardPlanId   → free | lite | pro | studio (UX messaging only; server is source of truth)
//   bcKeyboardBrain    → optional preferred brain name ("" = alt-me routes)
//   bcKeyboardApiBase  → optional API base override (else the keyboard defaults to prod)

import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";

export interface KeyboardArmState {
  apiKey?: string;
  planId?: string;
  brain?: string;
  apiBase?: string;
}

const KEYS = {
  apiKey: "bcKeyboardApiKey",
  planId: "bcKeyboardPlanId",
  brain: "bcKeyboardBrain",
  apiBase: "bcKeyboardApiBase",
} as const;

/** The keyboard extension only exists inside the native iOS shell. */
export function isKeyboardCapable(): boolean {
  return Capacitor.getPlatform() === "ios" && Capacitor.isNativePlatform();
}

/** Write the keyboard's credentials. Absent values are left untouched; brain is
 *  always written (empty string deliberately clears a prior preference). */
export async function armBCKeyboard(state: KeyboardArmState): Promise<void> {
  const setIfPresent = async (key: string, value?: string) => {
    if (value && value.length > 0) await Preferences.set({ key, value });
  };
  await setIfPresent(KEYS.apiKey, state.apiKey);
  await setIfPresent(KEYS.planId, state.planId);
  await setIfPresent(KEYS.apiBase, state.apiBase);
  if (state.brain !== undefined) {
    await Preferences.set({ key: KEYS.brain, value: state.brain });
  }
}

export async function readBCKeyboardState(): Promise<KeyboardArmState> {
  const get = async (key: string) => (await Preferences.get({ key })).value ?? undefined;
  return {
    apiKey: await get(KEYS.apiKey),
    planId: await get(KEYS.planId),
    brain: await get(KEYS.brain),
    apiBase: await get(KEYS.apiBase),
  };
}

/** Empty string is the native bridge's explicit "clear" signal — the keyboard
 *  falls back to its "sign in to wake your council" state. */
export async function disarmBCKeyboard(): Promise<void> {
  await Preferences.set({ key: KEYS.apiKey, value: "" });
}

export function isKeyboardArmed(state: KeyboardArmState): boolean {
  return !!(state.apiKey && state.apiKey.length > 0);
}
